import { apiClient } from '@/api/client';
import type { EligibleTour } from '@/features/feature-tours/types';
import { mapEligibleTour } from '../mappers/feature-tour';
import type {
  EligibleExperienceWire,
  EligibleExperiencesResponseWire,
  GuidedExperienceStateWire,
} from '../schemas/guided-experience';
import { eligibleExperiencesResponseWireSchema } from '../schemas/guided-experience';

/**
 * Result of getEligibleExperiences.
 *
 * NOTE: `tours` is sealed to the domain EligibleTour[] here. `experiences`
 * intentionally remains raw wire (snake_case) — several onboarding and
 * policy-acceptance hooks still read it as wire (e.g. `.experience_id`).
 * Sealing `experiences` is a separate future seam.
 */
export interface EligibleExperiencesResult {
  experiences: EligibleExperienceWire[];
  tours: EligibleTour[];
}

export async function getEligibleExperiences(): Promise<EligibleExperiencesResult> {
  const raw = await apiClient.get<EligibleExperiencesResponseWire>(
    '/api/v1/guided-experiences/eligible'
  );
  const validated = eligibleExperiencesResponseWireSchema.parse(raw);
  return {
    experiences: validated.experiences,
    tours: validated.tours.map(mapEligibleTour),
  };
}

export async function getExperienceState(experienceId: string): Promise<GuidedExperienceStateWire> {
  return apiClient.get<GuidedExperienceStateWire>(
    `/api/v1/guided-experiences/${experienceId}/state`
  );
}

export async function startExperience(
  experienceId: string,
  version?: number
): Promise<GuidedExperienceStateWire> {
  return apiClient.post<GuidedExperienceStateWire>(
    `/api/v1/guided-experiences/${experienceId}/start`,
    version !== undefined ? { experience_version: version } : {}
  );
}

export async function updateExperienceState(
  experienceId: string,
  data: { current_step?: number; payload?: string }
): Promise<GuidedExperienceStateWire> {
  return apiClient.patch<GuidedExperienceStateWire>(
    `/api/v1/guided-experiences/${experienceId}/state`,
    data
  );
}

export async function dismissExperience(
  experienceId: string
): Promise<GuidedExperienceStateWire> {
  return apiClient.post<GuidedExperienceStateWire>(
    `/api/v1/guided-experiences/${experienceId}/dismiss`,
    {}
  );
}

export async function completeExperience(
  experienceId: string
): Promise<GuidedExperienceStateWire> {
  return apiClient.post<GuidedExperienceStateWire>(
    `/api/v1/guided-experiences/${experienceId}/complete`,
    {}
  );
}
